import React, { useEffect, useState } from 'react';
import { Box, Typography, Container, Grid, useTheme, useMediaQuery, Card, CardContent, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Tooltip } from '@mui/material';
import { Icon } from '@iconify/react';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import CheckerApprovalModal from '../components/CheckerApprovalModal';
import { useToast } from '../components/ui/ToastProvider';
interface CheckerInboxProps {
  onLogout: () => void;
}
interface RecordData {
  id: number;
  fullName: string;
  username: string;
  email: string;
  mobile: string;
  currency: string;
  branchCode: string;
  settlementAccount: string;
  designation: string;
  status: string;
  batchId: string;
  fileName: string;
  submittedAt: string;
  approvalStatus: 'pending' | 'completed' | 'rejected';
}
export default function CheckerInbox({
  onLogout
}: CheckerInboxProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [records, setRecords] = useState<RecordData[]>([]);
  const [selectedRecord, setSelectedRecord] = useState<RecordData | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState<'complete' | 'reject'>('complete');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    showToast
  } = useToast();
  useEffect(() => {
    // Load records from localStorage
    const storedRecords = JSON.parse(localStorage.getItem('checkerInboxRecords') || '[]');
    setRecords(storedRecords.filter((r: RecordData) => r.approvalStatus === 'pending'));
  }, []);
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const handleOpenModal = (record: RecordData, action: 'complete' | 'reject') => {
    setSelectedRecord(record);
    setModalAction(action);
    setModalOpen(true);
  };
  const handleSubmitApproval = (reason: string) => {
    if (!selectedRecord) return;
    const allCheckerRecords = JSON.parse(localStorage.getItem('checkerInboxRecords') || '[]');
    // Update the record status in checker inbox
    const updatedCheckerRecords = allCheckerRecords.map((r: RecordData) => r.id === selectedRecord.id ? {
      ...r,
      approvalStatus: modalAction === 'complete' ? 'completed' : 'rejected',
      checkerReason: reason,
      checkedAt: new Date().toISOString()
    } : r);
    localStorage.setItem('checkerInboxRecords', JSON.stringify(updatedCheckerRecords));
    // Also update in maker inbox
    const allMakerRecords = JSON.parse(localStorage.getItem('makerInboxRecords') || '[]');
    const updatedMakerRecords = allMakerRecords.map((r: RecordData) => r.id === selectedRecord.id ? {
      ...r,
      approvalStatus: modalAction === 'complete' ? 'completed' : 'rejected',
      checkerReason: reason,
      checkedAt: new Date().toISOString()
    } : r);
    localStorage.setItem('makerInboxRecords', JSON.stringify(updatedMakerRecords));
    // Update local state
    setRecords(updatedCheckerRecords.filter((r: RecordData) => r.approvalStatus === 'pending'));
    showToast(`Record ${modalAction === 'complete' ? 'completed' : 'rejected'} successfully`, 'success');
    setModalOpen(false);
    setSelectedRecord(null);
  };
  return <Box sx={{
    width: '100%',
    height: '100%',
    bgcolor: 'background.default'
  }} data-id="element-3256" data-mp="1irrprzvqaf82">
      <TopBar onMenuClick={handleDrawerToggle} onLogout={onLogout} data-id="element-3257" data-mp="1epn0teg866rr" />
      <Sidebar open={mobileOpen} onClose={handleDrawerToggle} activePage="checker-inbox" data-id="element-3258" data-mp="2b8c8dvsdy8nj" />
      <Box component="main" sx={{
      flexGrow: 1,
      p: 3,
      width: {
        xs: '100%',
        sm: `calc(100% - ${240}px)`
      },
      ml: {
        xs: 0,
        sm: `${240}px`
      },
      mt: 8
    }} data-id="element-3259" data-mp="26hlz47gu9lnk">
        <Container maxWidth="xl" data-id="element-3260" data-mp="1hzy14viskkhm">
          <Grid container spacing={2} alignItems="center" sx={{
          mb: 3
        }} data-id="element-3261" data-mp="1i0zbexrzh2ya">
            <Grid item xs={12} data-id="element-3262" data-mp="29q2gugvlwjci">
              <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }} data-id="element-3263" data-mp="29qvnlhukf8un">
                <Box sx={{
                display: 'flex',
                alignItems: 'center'
              }} data-id="element-3264" data-mp="20xs4ou15i3sm">
                  <Icon icon="lucide:check-square" width={24} height={24} color="#0EA5E9" data-id="element-3265" data-mp="2soyc7vj7b3rx" />
                  <Typography variant="h5" component="h1" sx={{
                  ml: 1.5,
                  fontWeight: 600
                }} data-id="element-3266" data-mp="m0fukldtewpm">
                    Checker Inbox
                  </Typography>
                </Box>
                <Chip label={`${records.length} Pending`} sx={{
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                color: '#3B82F6',
                fontWeight: 600
              }} data-id="element-3267" data-mp="qsz5fal4bdqq" />
              </Box>
            </Grid>
          </Grid>
          {records.length === 0 ? <Card data-id="element-3268" data-mp="80n66wprgskt">
              <CardContent sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 400,
            textAlign: 'center'
          }} data-id="element-3269" data-mp="804y92tqkpx2">
                <Icon icon="lucide:check-square" width={80} height={80} color="#CBD5E1" style={{
              marginBottom: 24
            }} data-id="element-3270" data-mp="1qj04sm9vyb7t" />
                <Typography variant="h6" color="text.secondary" sx={{
              mb: 1
            }} data-id="element-3271" data-mp="25h2bh5c7rcta">
                  No Pending Records
                </Typography>
                <Typography variant="body2" color="text.secondary" data-id="element-3272" data-mp="1cq0tkwh4z362">
                  Records awaiting checker approval will appear here
                </Typography>
              </CardContent>
            </Card> : <Card data-id="element-3273" data-mp="p2sks4ki1bza">
              <TableContainer component={Paper} variant="outlined" data-id="element-3274" data-mp="p3c51rxmgcgh">
                <Table data-id="element-3275" data-mp="u1new2nx2myp">
                  <TableHead data-id="element-3276" data-mp="u0u5sv1hjlde">
                    <TableRow data-id="element-3277" data-mp="u02mail5xe3h">
                      <TableCell sx={{
                    fontWeight: 600
                  }} data-id="element-3278" data-mp="tzap53bx38b3">
                        Full Name
                      </TableCell>
                      <TableCell sx={{
                    fontWeight: 600
                  }} data-id="element-3279" data-mp="2yapblq64xtl0">
                        Username
                      </TableCell>
                      <TableCell sx={{
                    fontWeight: 600
                  }} data-id="element-3280" data-mp="2oe2pyz5ziyhx">
                        Email
                      </TableCell>
                      <TableCell sx={{
                    fontWeight: 600
                  }} data-id="element-3281" data-mp="2fkz72bckltfw">
                        Mobile
                      </TableCell>
                      <TableCell sx={{
                    fontWeight: 600
                  }} data-id="element-3282" data-mp="25oe2fr4gqkcl">
                        Branch Code
                      </TableCell>
                      <TableCell sx={{
                    fontWeight: 600
                  }} data-id="element-3283" data-mp="3tjraa3fp7gf8">
                        Batch
                      </TableCell>
                      <TableCell sx={{
                    fontWeight: 600
                  }} data-id="element-3284" data-mp="3khi1bi02vv3y">
                        Submitted
                      </TableCell>
                      <TableCell align="center" sx={{
                    fontWeight: 600
                  }} data-id="element-3285" data-mp="3aly9p7sjril0">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody data-id="element-3286" data-mp="2mx56qrdicagw">
                    {records.map(record => <TableRow key={record.id} hover data-id="element-3287" data-mp="2rt40yvliomp8">
                        <TableCell data-id="element-3288" data-mp="287kdzeicrwse">{record.fullName}</TableCell>
                        <TableCell data-id="element-3289" data-mp="289x1e76vqzcu">{record.username}</TableCell>
                        <TableCell data-id="element-3290" data-mp="247tm17h3bama">{record.email}</TableCell>
                        <TableCell data-id="element-3291" data-mp="2i6t1boipqd5s">{record.mobile}</TableCell>
                        <TableCell data-id="element-3292" data-mp="2i46whwn7ibr9">
                          <Chip label={record.branchCode} size="small" sx={{
                      backgroundColor: 'rgba(6, 182, 212, 0.1)',
                      color: '#06B6D4',
                      fontWeight: 600
                    }} data-id="element-3293" data-mp="2d6nimxuf80zu" />
                        </TableCell>
                        <TableCell data-id="element-3294" data-mp="2vypr3lwwsaz0">
                          <Typography variant="body2" fontSize="0.85rem" data-id="element-3295" data-mp="2vzrd2lfgqhvi">
                            {record.fileName}
                          </Typography>
                        </TableCell>
                        <TableCell data-id="element-3296" data-mp="35xoy7020zyjw">
                          <Typography variant="body2" fontSize="0.85rem" data-id="element-3297" data-mp="yfxtftkq37j9">
                            {new Date(record.submittedAt).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" data-id="element-3298" data-mp="orfulhw07ai9">
                          <Box sx={{
                      display: 'flex',
                      gap: 1,
                      justifyContent: 'center'
                    }} data-id="element-3299" data-mp="oo1vciimzhf1">
                            <Tooltip title="Complete" data-id="element-3300" data-mp="1cgi9ihz96p92">
                              <IconButton size="small" onClick={() => handleOpenModal(record, 'complete')} sx={{
                          color: '#10B981',
                          '&:hover': {
                            backgroundColor: 'rgba(16, 185, 129, 0.1)'
                          }
                        }} data-id="element-3301" data-mp="18dvp324ww6lw">
                                <Icon icon="lucide:check-circle" width={20} height={20} data-id="element-3302" data-mp="28khcgh5ij4sn" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject" data-id="element-3303" data-mp="2mg2z4ho9xchw">
                              <IconButton size="small" onClick={() => handleOpenModal(record, 'reject')} sx={{
                          color: '#EF4444',
                          '&:hover': {
                            backgroundColor: 'rgba(239, 68, 68, 0.1)'
                          }
                        }} data-id="element-3304" data-mp="2rc0d4hc2e6ri">
                                <Icon icon="lucide:x-circle" width={20} height={20} data-id="element-3305" data-mp="24bjbxh08cecz" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>}
        </Container>
      </Box>
      {selectedRecord && <CheckerApprovalModal open={modalOpen} onClose={() => {
      setModalOpen(false);
      setSelectedRecord(null);
    }} record={selectedRecord} action={modalAction} onSubmit={handleSubmitApproval} data-id="element-3306" data-mp="3214ja0940hxb" />}
    </Box>;
}